import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Card,
  CardContent,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop
} from '@mui/material';

import { Add, Close, Extension, Edit, Delete } from '@material-ui/icons';
import AddonForm from './AddonForm';
import { RatingSummary } from '../shared/allowedRelationships';


const AddonsTab = (props) => {
  const [openAddonModal, setOpenAddonModal] = useState(false);
  const [selectedAddonForEdit, setSelectedAddonForEdit] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [addonToDelete, setAddonToDelete] = useState(null);

  // State for notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Handle success states
  useEffect(() => {
    if (props.isSuccess) {
      setSnackbarMessage('Add-on operation completed successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenAddonModal(false);
      setSelectedAddonForEdit(null);
      
      // Reset success state after showing notification
      setTimeout(() => {
        props.resetAddonSuccess && props.resetAddonSuccess();
      }, 100);
    }
  }, [props.isSuccess]);

  // Handle error states
  useEffect(() => {
    if (props.isError) {
      setSnackbarMessage(`Error: ${props.isError.message || 'Something went wrong'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      
      // Reset error state after showing notification
      setTimeout(() => {
        props.resetAddonError && props.resetAddonError();
      }, 100);
    }
  }, [props.isError]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const currency = props.currency;

  const formatCurrency = (amountInCents) => {
    if (!amountInCents && amountInCents !== 0) return 'N/A';
    var units = (amountInCents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return currency ? currency + ' ' + units : units;
  };

  const formatEligibleRelationships = (allowedRelationships) => {
    if (!allowedRelationships || !Array.isArray(allowedRelationships)) return [];
    return allowedRelationships.map(r => r && r.relationship).filter(Boolean);
  };

  const handleOpenAddonModal = (addonData = null) => {
    setSelectedAddonForEdit(addonData);
    setOpenAddonModal(true);
  };

  const handleCloseAddonModal = () => {
    setOpenAddonModal(false);
    setSelectedAddonForEdit(null);
  };

  const handleCreateAddon = (data) => {
    if (selectedAddonForEdit) {
      // Edit mode
      if (props.handleEditAddons) {
        console.log("Editing Addon from AddonsTab:", data);
        const editData = {
          ...data,
          add_on_guid: selectedAddonForEdit.guid || selectedAddonForEdit.add_on_guid,
        };
        props.handleEditAddons(editData);
      }
    } else {
      // Create mode
      if (props.handleAddAddons) {
        console.log("Creating Addon from AddonsTab:", data);
        props.handleAddAddons(data);
      }
    }
    
    // Close modal and reset state
    setOpenAddonModal(false);
    setSelectedAddonForEdit(null);
  };

  const handleDeleteAddon = (addon_guid) => {
    if (props.handleDeleteAddons) {
      props.handleDeleteAddons(addon_guid);
    }
  };

  const handleDeleteClick = (addon) => {
    setAddonToDelete(addon);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (addonToDelete) {
      handleDeleteAddon(addonToDelete.guid);
      setDeleteConfirmOpen(false);
      setAddonToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setAddonToDelete(null);
  };

  const hasAddons = props.addons && props.addons.length > 0;

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Add-ons Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define optional coverage enhancements
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenAddonModal()}
            sx={{
            textTransform: "none",
            backgroundColor: "#fba92c",
            "&:hover": {
              backgroundColor: "#fba82c",
            },
            px: 3,
            py: 1.5,
            borderRadius: 2,
          }}
          >
            Add Add-on
          </Button>
        </Stack>
      </Box>

      {hasAddons ? (
        <Box>
          <Stack spacing={3}>
            {props.addons.map((addon, index) => (
              <Card 
                key={index} 
                sx={{ 
                  border: "1px solid #e5e7eb", 
                  borderRadius: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    borderColor: "#C9C9C9",
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "#F8F9FA",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}>
                        <Extension sx={{ fontSize: 20, color: "#173E4B" }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {addon.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                          {addon.code}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton 
                        size="small" 
                        sx={{ color: "#6b7280" }}
                        onClick={() => handleOpenAddonModal(addon)}
                        title="Edit add-on"
                      >
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        sx={{ color: "#ef4444" }}
                        onClick={() => handleDeleteClick(addon)}
                        title="Delete add-on"
                      >
                        <Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Add-on Details */}
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, mb: 3 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Benefit Amount
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(addon.benefit_amount_in_cents)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {addon.granularity} • Cardinality: {addon.cardinality}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Premium Amount
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(addon.premium_amount_in_cents)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {addon.premium_granularity} • Cardinality: {addon.premium_cardinality}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Waiting Period
                        </Typography>
                        <Typography variant="body2">
                          {addon.waiting_period_cardinality !== undefined && addon.waiting_period_cardinality !== null
                            ? addon.waiting_period_cardinality + ' ' + (addon.waiting_period_granularity || 'Day')
                            : (addon.waiting_period_days || 0) + ' days'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Eligible Relationships */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: "block" }}>
                        Eligible For
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {formatEligibleRelationships(addon.allowed_relationships).length > 0 ? (
                          <Typography variant="body2">
                            {formatEligibleRelationships(addon.allowed_relationships).join(', ')}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                            No eligibility defined
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Rating nodes attached to this add-on */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: "block" }}>
                      Rating
                    </Typography>
                    <RatingSummary
                      coverRatingNodeGuid={addon.cover_rating_node_guid}
                      premiumRatingNodeGuid={addon.premium_rating_node_guid}
                      waitingPeriodRatingNodeGuid={addon.waiting_period_rating_node_guid}
                      ratingNodes={props.ratingNodes}
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      ) : (
        <Box
          sx={{
            border: "2px dashed #d1d5db",
            borderRadius: 2,
            p: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 300,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: "#f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Extension sx={{ fontSize: 24, color: "#6b7280" }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            No add-ons yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
            Get started by creating your first add-on.<br />
            Add-ons provide optional coverage enhancements for your product.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => handleOpenAddonModal()}
            sx={{
              textTransform: "none",
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
              px: 3,
              py: 1,
            }}
          >
            Create Add-on
          </Button>
        </Box>
      )}

      {/* Add-on Creation/Edit Modal */}
      <Dialog
        open={openAddonModal && !props.isLoading}
        onClose={handleCloseAddonModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedAddonForEdit ? "Edit Add-on" : "Create New Add-on"}
            </Typography>
            <IconButton onClick={handleCloseAddonModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3 }}>
          <AddonForm
            handleAddAddon={handleCreateAddon}
            initialData={selectedAddonForEdit}
            isEditMode={!!selectedAddonForEdit}
            ratingNodes={props.ratingNodes || []}
            currency={currency}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen &&  !props.isLoading}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Delete Add-on
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the add-on "{addonToDelete && addonToDelete.name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCancelDelete}
            sx={{ 
              textTransform: "none",
              color: "#6b7280"
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{ 
              textTransform: "none",
              backgroundColor: "#ef4444",
              "&:hover": {
                backgroundColor: "#dc2626",
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={props.isLoading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Processing add-on operation...
          </Typography>
        </Box>
      </Backdrop>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddonsTab;